import React from 'react'
import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { createBreadcrumbWithI18n } from '~/apps/ninesegsAnalyzer/routes/paths'
import { HStack } from '~/components/elements/HStack'
import {
  PageContainer,
  type PageContainerProps,
} from '~/components/elements/PageContainer'
import { VStack } from '~/components/elements/VStack'

import { useBrandAccountId } from '../hooks/useBrandAccountId'
import { useStrategyMemoId } from '../hooks/useStrategyMemoId'
import { useTabulationQuery } from '../hooks/useTabulationQuery'
import { type InputValues } from '../utils'

import { BrandGroupInput } from './BrandGroupInput'
import { BrandInput } from './BrandInput'
import { DataDisplayTypeInput } from './DataDisplayTypeInput'
import { GoalInput } from './GoalInput'
import { LastSavedDisplay } from './LastSavedDisplay'
import { StrategiesContainer } from './StrategiesContainer'
import { StrategyMemoArchiveButton } from './StrategyMemoArchiveButton'
import { StrategyMemoCloneButton } from './StrategyMemoCloneButton'
import styles from './StrategyMemoEdit.css'
import { StrategyMemoSaveButton } from './StrategyMemoSaveButton'
import { StrategyMemoShareButton } from './StrategyMemoShareButton'
import { SurveyFilterInput } from './SurveyFilterInput'
import { SurveyInput } from './SurveyInput'
import { TitleInput } from './TitleInput'

export const StrategyMemoForm = () => {
  const { watch } = useFormContext<InputValues>()

  const { t } = useTranslation()
  const brandAccountId = useBrandAccountId()
  const strategyMemoId = useStrategyMemoId()
  const title = watch('title')

  const createBreadcrumb = React.useMemo(() => createBreadcrumbWithI18n(t), [t])
  const breadcrumbs = React.useMemo<PageContainerProps['breadcrumbs']>(
    () => [
      createBreadcrumb('top', {}),
      createBreadcrumb('strategyMemoManagement'),
      createBreadcrumb('strategyMemoList', { brandAccountId }),
      createBreadcrumb(
        'strategyMemoEdit',
        { brandAccountId, strategyMemoId },
        title,
      ),
    ],
    [brandAccountId, createBreadcrumb, strategyMemoId, title],
  )

  const { isFetching } = useTabulationQuery()

  return (
    <PageContainer breadcrumbs={breadcrumbs} executing={isFetching}>
      <VStack spacing={2} verticalAppearance="stretch">
        <VStack flex="none" spacing={2}>
          <HStack align="center" spacing={2}>
            <SurveyInput />

            <BrandGroupInput />

            <BrandInput />

            <SurveyFilterInput />
          </HStack>

          <HStack align="end" justify="space-between" spacing={2}>
            <DataDisplayTypeInput />

            <HStack spacing={1}>
              <StrategyMemoSaveButton />

              <StrategyMemoShareButton />

              <StrategyMemoCloneButton />

              <StrategyMemoArchiveButton />
            </HStack>
          </HStack>
        </VStack>

        <VStack flex={1} spacing={4} verticalAppearance="scroll">
          <VStack spacing={2}>
            <TitleInput />

            <GoalInput />
          </VStack>

          <StrategiesContainer />
        </VStack>

        <HStack
          align="center"
          className={styles.footer}
          justify="start"
          spacing={4}
        >
          <LastSavedDisplay />
        </HStack>
      </VStack>
    </PageContainer>
  )
}
